import React, {FC, useContext, useState} from 'react'
import {downloadDocument} from '../../../webservices/demandes/demandesActions'
import {CircularProgress, IconButton, ListItemText, Stack, Typography, useTheme} from '@mui/material'
import {Field, Form} from 'react-final-form'
import {FormattedMessage, useIntl} from 'react-intl'
import FileInput from '../../../../components/fileInput/FileInput'
import Icon from '@mdi/react'
import {mdiAccount, mdiDownload, mdiFileDocument} from '@mdi/js'
import ButtonDefault from '../../../../components/ButtonDefault'
import {DEMAND_FIELDS, DemandeBean, DocumentBean, PieceBean} from './demandeConstants'
import {styled} from '@mui/material/styles'
import ContactService from '../../../../api/gerep/ContactService'
import LocalStorage from '../../../../business/storage/LocalStorage'
import {ToastType} from '../../../../components/toast/toastConstants'
import {ToastContext} from '../../../../components/toast/ToastContext'
import {refreshDemandes} from '../../personalInfo/services/suggest/suggestActions'
import {useDispatch} from 'react-redux'
import {FormApi} from 'final-form'
import Popup from '../../../../components/Popup'
import {useHistory} from "react-router-dom";
import {SECONDARY_PATH} from "../../../../constant/path";

type DemandeAccordionFormProps = {
    demande: DemandeBean,
    assurePrincipal: any
}

const ETAT_PAYE = "Payé(e)"

const StackDocument = styled(Stack)(() => ({
    borderRadius: '4px',
    border: `1px solid #DEDEDE`
}))

const DemandeAccordionForm: FC<DemandeAccordionFormProps> = (
    {
        demande,
        assurePrincipal
    }
) => {
    const intl = useIntl()
    const theme = useTheme()
    const history = useHistory()
    const dispatch = useDispatch()
    const {addToast} = useContext(ToastContext)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [open, setOpen] = useState<boolean>(false)
    const [uploadReport, setUploadReport] = useState<{ message: string, success: boolean, piece: string }[]>([])

    const onSubmit = (values: { pieces: PieceBean[] }, form: FormApi<any>) => {
        setIsLoading(true)

        const {pieces} = values
        const parcours: {
            idpiece: number,
            libellePiece: string,
            entite: number | undefined,
            contexte: number,
            numbene: number,
            nombene: string,
            nopiece: number
        } [] = []

        const formFields: any[] = []
        demande.documentsEnAttente.forEach((document) => {
            parcours.push({
                idpiece: document.idPiece,
                libellePiece: document.libPiece,
                entite: document.entite,
                contexte: document.contexte,
                numbene: demande.numbene,
                nombene: demande.beneficiaire,
                nopiece: document.noPiece
            })
            const pieceTosend = pieces.filter(piece => piece[DEMAND_FIELDS.ID_PIECE] === document.idPiece)[0]

            if (pieceTosend && pieceTosend.files) {
                formFields.push({
                    key: document.idPiece,
                    value: pieceTosend.files ? pieceTosend.files : []
                })
            }
        })

        formFields.push({
            key: 'details',
            value: {parcours}
        })
        formFields.push({
            key: 'idDemande',
            value: demande.idDemande
        })

        return ContactService.sendPieces(LocalStorage.getToken(), formFields)
            .then((response: { message: string, success: boolean, piece: string }[]) => {
                const displayReport = response.some(report => !report.success)
                if (!displayReport) { // Tous les documents ont été envoyés
                    addToast(ToastType.SUCCESS, 'suivie_Demande.form.success')
                    refreshDemandes(dispatch)
                    form.reset()
                } else { // Certains documents sont en erreur
                    setUploadReport(response)
                    setOpen(true)
                }
            })
            .catch((e) => {
                if (e?.data?._error) {
                    addToast(ToastType.ERROR, e.data._error)
                } else if (e?.data) {
                    return e.data
                } else {
                    addToast(ToastType.ERROR, 'Echec de la demande')
                }
            })
            .finally(() => setIsLoading(false))
    }

    const getCommentaire = (docs: any[]): string | null => {
        return docs.find(doc => doc.commentaire != null)?.commentaire ?? null
    }

    const documents = demande.documents ? demande.documents.filter(doc => !doc.aConsulter) : []
    const documentsAConsulter = demande.documents ? demande.documents.filter(doc => doc.aConsulter === true) : []
    const documentsEnAttente = demande.documentsEnAttente

    const displayDcuments = (docsAAfficher: DocumentBean[], aConsulter: boolean = false) => {
        return (<Stack direction="column" spacing="8px">
            <Typography variant="h4">
                <FormattedMessage
                    id={aConsulter ? "suivie_Demande.form.titre.aConsulter" : "suivie_Demande.form.titre.deposes"}/>
            </Typography>
            {
                docsAAfficher && docsAAfficher.map((document: any) => (
                    <StackDocument direction="row" display="flex" alignItems="center" padding="4px 12px"
                                   justifyContent="space-between"
                                   style={{...(aConsulter && {backgroundColor: '#ECF3FD',})}}>
                        <Stack direction="row" spacing="8px" alignItems="center">
                            <Icon path={mdiFileDocument} color="#9D9C9C" size="24px"/>
                            <Typography flex={1} variant="body2" sx={{wordBreak: 'break-word'}}>
                                {document.nomdoc}
                            </Typography>
                        </Stack>
                        {document.url && <IconButton
                            onClick={(event) => {
                                event.preventDefault()
                                downloadDocument(document.url)
                            }}>
                            <Icon path={mdiDownload} color={theme.palette.info.main} size="24px"/>
                        </IconButton>}
                    </StackDocument>
                ))
            }
            {getCommentaire(docsAAfficher) &&
                <Typography variant="body2" style={{wordBreak: 'break-word', fontStyle: 'italic'}}>
                    <FormattedMessage id="suivie_Demande.form.commentaire"
                                      values={{commentaire: getCommentaire(docsAAfficher)}}/>
                </Typography>
            }
        </Stack>)
    }

    return (
        <Form
            onSubmit={onSubmit}
            validate={(values) => {
                const errors: any = {}
                if (!values[DEMAND_FIELDS.PIECES]) {
                    values[DEMAND_FIELDS.PIECES] = []
                }

                const filesErrors: { files: string }[] = []
                documentsEnAttente && documentsEnAttente.forEach((_, index) => {
                    const fileValue = values[DEMAND_FIELDS.PIECES]?.[index]?.files
                    if (!fileValue || (Array.isArray(fileValue) && fileValue.length === 0)) {
                        filesErrors[index] = {files: 'Ce document est obligatoire'}
                    }
                })
                if (filesErrors.length > 0) {
                    errors[DEMAND_FIELDS.PIECES] = filesErrors
                }
                return errors
            }}
            render={({handleSubmit, form}) => (
                <form id={`demandeForm_${demande.id}`} onSubmit={handleSubmit}
                      style={{width: '100%', display: 'flex', flexDirection: 'column'}}>
                    <Stack direction="column" spacing="24px">
                        {
                            demande.numDemandeur && assurePrincipal.numIndividu !== demande.numDemandeur &&
                            <Stack direction="row" spacing="8px">
                                <Icon path={mdiAccount} color="#9D9C9C" size="24px"/>
                                <Typography variant="subtitle1">
                                    <FormattedMessage id="suivie_Demande.form.par"
                                                      values={{demandeur: demande.demandeur}}/>
                                </Typography>
                            </Stack>
                        }
                        {
                            demande.contenuMail && <Stack direction="column" spacing="8px">
                                <Typography variant="h4">
                                    <FormattedMessage id="suivie_Demande.form.titre.mail"/>
                                </Typography>
                                <Typography variant="body2">
                                    {demande.contenuMail}
                                </Typography>
                            </Stack>
                        }
                        {documentsEnAttente && documentsEnAttente.length > 0 && <Stack direction="column" spacing="8px">
                            <Typography variant="h4">
                                <FormattedMessage id="suivie_Demande.form.titre.enAttente"/>
                            </Typography>

                            {documentsEnAttente.map((doc, index) => {
                                const fieldName = `${DEMAND_FIELDS.PIECES}[${index}]`
                                return (
                                    <Stack key={`files_${index}`} spacing="4px">
                                        <FileInput
                                            name={`${fieldName}.${DEMAND_FIELDS.FILES}`}
                                            title={doc.libPiece}
                                            label={doc.commentaire}
                                        />
                                        <Field
                                            name={`${fieldName}.${DEMAND_FIELDS.ID_PIECE}`}
                                            component="input"
                                            type="hidden"
                                            initialValue={doc.idPiece}
                                        />
                                    </Stack>
                                )
                            })}
                        </Stack>}
                        {documentsAConsulter && documentsAConsulter.length > 0 && displayDcuments(documentsAConsulter, true)}
                        {documents && documents.length > 0 && displayDcuments(documents)}
                        {
                            documentsEnAttente && documentsEnAttente.length > 0 && <Stack alignItems="center">
                                <ButtonDefault
                                    variant="contained"
                                    onClick={() => {
                                    }}
                                    type="submit"
                                >
                                    {isLoading ? <CircularProgress size={24} color="inherit"/> :
                                        <FormattedMessage id="global.button.send"/>}
                                </ButtonDefault>
                            </Stack>
                        }
                        {
                            demande.etat.libelleEtat === ETAT_PAYE && <Stack alignItems="center">
                                <ButtonDefault
                                    variant="contained"
                                    onClick={() => history.push(SECONDARY_PATH.REFUND_LIST)}
                                >
                                        <FormattedMessage id="suivie_Demande.versRemboursement"/>
                                </ButtonDefault>
                            </Stack>
                        }
                    </Stack>
                    <Popup
                        width={'800px'}
                        open={open}
                        setOpen={setOpen}
                        onClose={() => {
                            setOpen(false)
                            refreshDemandes(dispatch)
                            form.reset()
                        }}
                        title={intl.formatMessage({id: 'suivie_Demande.popin.titre'})}
                    >
                        <Typography variant="body1">
                            <FormattedMessage id="suivie_Demande.popin.pieces"/>
                        </Typography>
                        <ul>
                            {uploadReport.map((item, index) => (
                                <li key={index}>
                                    <ListItemText
                                        primary={item.piece}
                                        secondary={intl.formatMessage({
                                            id: item.success ? 'suivie_Demande.popin.envoyee' : 'suivie_Demande.popin.echec'
                                        }, {message: item.message})}
                                    />
                                </li>
                            ))}
                        </ul>
                    </Popup>
                </form>
            )}
        />
    )
}

export default DemandeAccordionForm
